"use client";

import { useState, useEffect, useRef, useCallback, Suspense } from "react";

import { MapPin, Filter, X, Loader2, ChevronUp } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { hotelService } from "@/services/hotel-search/hotel.service";
import type { MappedHotel, HotelListApiResponse } from "@/types/hotel";
import type { HotelFilters, FilterLookups } from "@/types/hotel-filters";
import HotelSearchBar from "@/components/HotelSearch/HotelSearchBar";
import { FilterSidebar } from "./components/FilterSidebar";
import { HotelCard } from "./components/HotelCard";

const HotelsPageContent: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  //Add this hook near the top of HotelsPageContent:
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const [hotels, setHotels] = useState<MappedHotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [sortBy, setSortBy] = useState<string>("Best match");
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 20;
  const isFirstFilterEffect = useRef(true);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [filters, setFilters] = useState<HotelFilters>({
    searchText: "",
    priceRange: { min: 0, max: 500000 },
    categories: [],
    amenities: [],
    accessModes: [],
    paymentMethods: [],
    starRatings: [],
  });

  const [lookups, setLookups] = useState<FilterLookups | null>(null);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const data = await hotelService.getFilterLookups("");
        if (data) {
          setLookups(data);
        }
      } catch (error) {
        console.error("Failed to fetch filter lookups:", error);
        // Don't set error state, just continue without lookups
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
    setHotels([]);
    setHasMore(true);
  }, [
    searchParams.get("location"),
    searchParams.get("search"),
    sortBy,
    filters.searchText,
    filters.amenities,
    filters.starRatings,
    filters.priceRange,
    filters.categories,
    filters.accessModes,
    filters.paymentMethods,
  ]);

  useEffect(() => {
    const fetchHotels = async () => {
      const isFirstPage = page === 1;
      if (isFirstPage) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      try {
        const location = searchParams.get("location") || "";
        const locationId = searchParams.get("locationId");
        const searchParam = searchParams.get("search") || "";

        const params: any = {
          status: 2,
          page,
          pageSize: ITEMS_PER_PAGE,
        };

        if (location && location.trim()) {
          params.location = null;
        }

        if (location && location.trim()) {
          params.search = location.trim();
        }

        if (filters.amenities.length > 0) {
          params.amenities = filters.amenities.join(",");
        }

        if (filters.starRatings.length > 0) {
          const minStar = Math.min(...filters.starRatings);
          params.minStars = minStar;
        }

        // Add price range filters
        if (filters.priceRange.min > 0) {
          params.minAmount = filters.priceRange.min;
        }
        if (filters.priceRange.max > 0 && filters.priceRange.max < 1000000) {
          params.maxAmount = filters.priceRange.max;
        }
        // Add property types (categories) filter
        if (filters.categories.length > 0) {
          params.propertyTypes = filters.categories.join(",");
        }

        // Add access modes filter
        if (filters.accessModes.length > 0) {
          params.accessModes = filters.accessModes.join(",");
        }

        // Add payment methods filter
        if (filters.paymentMethods.length > 0) {
          params.paymentMethods = filters.paymentMethods.join(",");
        }

        if (sortBy === "Lowest price") {
          params.sortby = "price";
          params.sortorder = "asc";
        } else if (sortBy === "Top reviewed") {
          params.sortby = "rating";
          params.sortorder = "desc";
        } else if (sortBy === "Best match") {
          params.sortby = "popularity";
          params.sortorder = "desc";
        }

        const response = await hotelService.searchHotels(params);

        if (response.success && response.data) {
          const hotelListData = response.data as HotelListApiResponse;

          if (
            hotelListData.result?.items &&
            hotelListData.result.items.length > 0
          ) {
            const mappedHotels: MappedHotel[] = hotelListData.result.items.map(
              (hotel) => ({
                id: hotel.id,
                name: hotel.name,
                chainName: hotel.chainName,
                location: hotel.location,
                rating: hotel.reviewExcellence || 0,
                reviews: hotel.reviews || 0,
                price: hotel.priceFrom || 0,
                originalPrice: null,
                image: hotel.coverImageUrl || "/images/hotels/placeholder.jpg",
                amenities: hotel.amenities || [],
                availability: "",
                cancellation: "",
                stars: hotel.stars,
                availableQuantity: hotel.availableQuantity,
                remainingRooms: hotel.remainingRooms,
                badge:
                  (hotel.reviewExcellence || 0) >= 4.5
                    ? "Excellent"
                    : (hotel.reviewExcellence || 0) >= 4.0
                      ? "Very Good"
                      : "Good",
              }),
            );

            setHotels((prev) =>
              isFirstPage ? mappedHotels : [...prev, ...mappedHotels],
            );
            if (isFirstPage) {
              const total =
                hotelListData.result.paging?.total || mappedHotels.length;
              setTotalResults(total);
            }
            setHasMore(mappedHotels.length === ITEMS_PER_PAGE);
          } else {
            if (isFirstPage) {
              setHotels([]);
              setTotalResults(0);
            }
            setHasMore(false);
          }
        } else {
          if (isFirstPage) {
            setHotels([]);
            setTotalResults(0);
          }
          setHasMore(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        if (isFirstPage) {
          setHotels([]);
          setTotalResults(0);
        }
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchHotels();
  }, [
    searchParams.get("location"),
    searchParams.get("search"),
    sortBy,
    filters.searchText,
    filters.amenities,
    filters.starRatings,
    filters.priceRange,
    filters.categories,
    filters.accessModes,
    filters.paymentMethods,
    page,
  ]);

  const handleFilterChange = (newFilters: Partial<HotelFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleHotelClick = (hotelId: number) => {
    const checkIn = searchParams.get("checkIn");
    const checkOut = searchParams.get("checkOut");
    if (!checkIn || !checkOut) {
      document
        .getElementById("hotel-search-bar")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.dispatchEvent(new CustomEvent("lilylanka:require-dates"));
      return;
    }

    const location = searchParams.get("location");
    const locationId = searchParams.get("locationId");
    const adults = searchParams.get("adults");
    const children = searchParams.get("children");
    const rooms = searchParams.get("rooms");

    const params = new URLSearchParams();
    if (location) params.set("location", location);
    if (locationId) params.set("locationId", locationId);
    params.set("checkIn", checkIn);
    params.set("checkOut", checkOut);
    if (adults) params.set("adults", adults);
    if (children) params.set("children", children);
    if (rooms) params.set("rooms", rooms);

    window.open(
      `/hotels/${hotelId}?${params.toString()}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const currentLocation = searchParams.get("location");

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, hotels.length]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Bar */}
      <div
        id="hotel-search-bar"
        className="relative z-40 border-gray-100 pt-24 md:pt-28"
      >
        {" "}
        <div className="max-w-5xl mx-auto">
          <HotelSearchBar
            variant="list"
            transparent={false}
            onValuesChange={(values) => {
              if (values.searchText !== undefined) {
                setFilters((prev) => ({
                  ...prev,
                  searchText: values.searchText || "",
                }));
              }
            }}
          />
        </div>
      </div>

      {/* Mobile Filter Toggle */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <button
          onClick={() => setShowMobileFilters(true)}
          className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200"
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filters</span>
        </button>
      </div>

      {/* Mobile Filter Overlay */}
      {showMobileFilters && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden">
          <div
            className="absolute inset-0"
            onClick={() => setShowMobileFilters(false)}
          ></div>
          <div className="absolute left-0 top-0 h-full w-[88%] sm:w-96 max-w-sm bg-white overflow-y-auto shadow-xl animate-in slide-in-from-left duration-200">
            <FilterSidebar
              filters={filters}
              lookups={lookups}
              onFilterChange={handleFilterChange}
              onClose={() => setShowMobileFilters(false)}
            />
          </div>
        </div>
      )}

      {/* Sidebar + Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <FilterSidebar
              filters={filters}
              lookups={lookups}
              onFilterChange={handleFilterChange}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {(currentLocation || searchParams.get("search")) && !loading && (
              <div className="mb-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  {hotels.length > 0
                    ? `Hotels ${currentLocation ? `in ${currentLocation}` : searchParams.get("search") ? `matching "${searchParams.get("search")}"` : ""}
`
                    : `Searching for hotels ${
                        currentLocation ? `in ${currentLocation}` : ""
                      }`}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {totalResults > 0
                    ? `${totalResults} ${
                        totalResults === 1 ? "property" : "properties"
                      } found`
                    : "No properties found"}
                </p>
              </div>
            )}

            {/* Sort Bar */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm md:text-base text-gray-800 font-bold whitespace-nowrap">
                  Sort by
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSortBy("Best match")}
                    className={`px-4 py-2 text-sm font-medium rounded-full ${
                      sortBy === "Best match"
                        ? "bg-purple-600 text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    Best match
                  </button>
                  <button
                    onClick={() => setSortBy("Lowest price")}
                    className={`px-4 py-2 text-sm rounded-full ${
                      sortBy === "Lowest price"
                        ? "bg-purple-600 text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    Lowest price
                  </button>
                  <button
                    onClick={() => setSortBy("Top reviewed")}
                    className={`px-4 py-2 text-sm rounded-full ${
                      sortBy === "Top reviewed"
                        ? "bg-purple-600 text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    Top reviewed
                  </button>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <p className="mt-4 text-gray-600">Searching for hotels...</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="font-semibold text-red-900 mb-2">
                  Error loading hotels
                </p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Hotels */}
            {!loading && !error && (
              <div className="space-y-4">
                {hotels.length > 0 ? (
                  <>
                    {hotels.map((hotel) => (
                      <HotelCard
                        key={hotel.id}
                        hotel={hotel}
                        onClick={handleHotelClick}
                      />
                    ))}

                    {hasMore && (
                      <div
                        ref={loadMoreRef}
                        className="flex items-center justify-center py-6"
                      >
                        {loadingMore && (
                          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                        )}
                      </div>
                    )}

                    {!hasMore && (
                      <p className="text-center text-sm text-gray-400 py-6">
                        You've reached the end of the results.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      No hotels found
                    </p>
                    <p className="text-gray-600 max-w-md mx-auto">
                      {currentLocation
                        ? `We couldn't find any hotels matching "${currentLocation}". Try a different search term.`
                        : "Start searching for a location to see available hotels."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      {/* {!loading && !error && hotels.length > 0 && totalPages > 1 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>

            <div className="flex gap-2">
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                const showPage =
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);

                if (!showPage) {
                  if (
                    pageNum === currentPage - 3 ||
                    pageNum === currentPage + 3
                  ) {
                    return (
                      <span key={pageNum} className="px-2 py-2 text-gray-500">
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium ${
                      currentPage === pageNum
                        ? "bg-purple-600 text-white"
                        : "border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )} */}

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Scroll to top"
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-purple-600 text-white shadow-lg hover:bg-purple-700 transition-colors duration-200"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

const HotelsPage: React.FC = () => {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading hotels...</p>
          </div>
        </div>
      }
    >
      <HotelsPageContent />
    </Suspense>
  );
};

export default HotelsPage;
